import React, {useMemo, useRef} from 'react'
import {useTranslation} from 'react-i18next'
import {Spin} from 'antd'
import {formatNumber} from 'accounting'

import {
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  TimeScale,
  Title,
  Tooltip,
} from 'chart.js'
import {Line} from 'react-chartjs-2'

import KpiTabs from '../../../Components/KpiTabs/KpiTabs'
import {formatCurrency} from '../../../Utils/utils'
import {kpiChartOptions} from './chartsOptions'
import {getGradient, tooltipLine} from './chartsPlagins'

// This chart used to be the only Line on the page, and AlertsOverviewChart carried the
// registration. It still owns its own so the page renders whichever of the two mounts.
ChartJS.register(TimeScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler)

const money = (value) => formatCurrency(value ?? 0, true)

// Each tab merges two of the backend's blocks, so the backend's per-block tooltip only
// ever explains half of what the tab shows. The copy lives here instead, and covers the
// headline and the figure beneath it.
const TOOLTIP_KEYS = {
  total_alerts: 'Every alert raised in this period, and how many arrive on an average day.',
  alerts_volume: 'The order value those alerts put at risk, and the average amount on a single alert.',
  chargeback_prevented:
    'Alerts refunded before they became chargebacks, and the fee saved on each one.',
  credits_received: 'Credit requests the networks approved, and the total they credited back.',
}

// The parent (DashboardPage/index.js) decides whether to render this section at all vs its own
// "No data available yet.." fallback — it must agree with buildTabs on what "enough data" means,
// or a response missing just one of these four blocks makes the parent render KpiSection while
// KpiSection itself renders nothing (CHA1-1603: confirmed via a mocked partial response).
export const hasRequiredKpiBlocks = (blocks) => {
  const byKey = Object.fromEntries((blocks ?? []).map((block) => [block.key, block]))
  return !!(
    byKey.total_alerts &&
    byKey.alerts_volume &&
    byKey.chargeback_prevented &&
    byKey.credits_received
  )
}

// The backend sends six blocks; four of them lead a tab. The two it does not surface,
// fees saved and credited back, ride on the second line of the count they belong to. Each
// subhead states something the headline cannot: a rate of arrival, an average, or the
// money attached to the count above it.
const buildTabs = (blocks, t) => {
  if (!hasRequiredKpiBlocks(blocks)) return []

  const byKey = Object.fromEntries((blocks ?? []).map((block) => [block.key, block]))
  const {
    total_alerts: alerts,
    alerts_volume: volume,
    chargeback_prevented: prevented,
    chargeback_fees_saved: fees,
    credits_received: credits,
    credited_back: back,
  } = byKey

  // The series carries one point per day in the window, so its length is the window.
  const days = alerts.series?.length ?? 0
  const alertsPerDay = days ? alerts.value / days : 0
  const averageAlertAmount = alerts.value ? volume.value / alerts.value : 0

  return [
    {
      key: alerts.key,
      label: t('Total alerts'),
      value: alerts.value,
      tooltip: TOOLTIP_KEYS.total_alerts,
      secondary: t('_count_ a day on average', {count: formatNumber(alertsPerDay, 1, ',', '.')}),
      secondaryPrivate: true,
      series: alerts.series ?? [],
      breakdown: alerts.breakdown,
      comparisonValue: alerts.comparison_value,
      comparisonLabel: alerts.comparison_period?.label,
      comparisonSeries: alerts.comparison_series,
      comparisonLabels: alerts.comparison_labels,
      comparisonBreakdown: alerts.comparison_breakdown,
    },
    {
      key: volume.key,
      label: t('Total volume'),
      value: volume.value,
      prefix: '$',
      tooltip: TOOLTIP_KEYS.alerts_volume,
      secondary: t('_amount_ average alert amount', {amount: money(averageAlertAmount)}),
      secondaryPrivate: true,
      series: volume.series ?? [],
      breakdown: volume.breakdown,
      comparisonValue: volume.comparison_value,
      comparisonLabel: volume.comparison_period?.label,
      comparisonSeries: volume.comparison_series,
      comparisonLabels: volume.comparison_labels,
      comparisonBreakdown: volume.comparison_breakdown,
    },
    {
      key: prevented.key,
      label: t('Chargebacks prevented'),
      value: prevented.value,
      tooltip: TOOLTIP_KEYS.chargeback_prevented,
      secondary: t('_amount_ in fees saved', {amount: money(fees?.value)}),
      secondaryPrivate: true,
      series: prevented.series ?? [],
      breakdown: prevented.breakdown,
      // Chargebacks prevented is the one tab whose tooltip breaks down by card type
      // (Visa/Mastercard/Amex/Discover/Other) instead of by network - see kpiChartOptions.
      cardBreakdown: prevented.card_breakdown,
      comparisonValue: prevented.comparison_value,
      comparisonLabel: prevented.comparison_period?.label,
      comparisonSeries: prevented.comparison_series,
      comparisonLabels: prevented.comparison_labels,
      comparisonBreakdown: prevented.comparison_breakdown,
      comparisonCardBreakdown: prevented.comparison_card_breakdown,
    },
    {
      key: credits.key,
      label: t('Credits received'),
      value: credits.value,
      tooltip: TOOLTIP_KEYS.credits_received,
      secondary: t('_amount_ credited back', {amount: money(back?.value)}),
      secondaryPrivate: true,
      series: credits.series ?? [],
      breakdown: credits.breakdown,
      comparisonValue: credits.comparison_value,
      comparisonLabel: credits.comparison_period?.label,
      comparisonSeries: credits.comparison_series,
      comparisonLabels: credits.comparison_labels,
      comparisonBreakdown: credits.comparison_breakdown,
    },
  ]
}

const KpiSection = ({blocks, labels, selectedKey, onSelect, theme, loading}) => {
  const {t} = useTranslation()
  const chartRef = useRef(null)

  const tabs = useMemo(() => buildTabs(blocks, t), [blocks, t])
  const selected = tabs.find((tab) => tab.key === selectedKey) ?? tabs[0]

  const chartOptions = useMemo(
    () =>
      kpiChartOptions(theme, {
        key: selected?.key,
        prefix: selected?.prefix,
        label: selected?.label,
        breakdown: selected?.breakdown,
        comparisonBreakdown: selected?.comparisonBreakdown,
        cardBreakdown: selected?.cardBreakdown,
        comparisonCardBreakdown: selected?.comparisonCardBreakdown,
      }),
    [
      theme,
      selected?.key,
      selected?.prefix,
      selected?.label,
      selected?.breakdown,
      selected?.comparisonBreakdown,
      selected?.cardBreakdown,
      selected?.comparisonCardBreakdown,
    ]
  )

  // Only trustworthy when it lines up point-for-point with `labels` - a mismatched length
  // would plot the dashed line against the wrong days, so it's safer to drop it than guess.
  const comparisonSeries =
    selected?.comparisonSeries?.length === labels?.length ? selected.comparisonSeries : null
  const comparisonLabels =
    selected?.comparisonLabels?.length === labels?.length ? selected.comparisonLabels : null

  const chartData = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: selected?.label ?? '',
          data: selected?.series ?? [],
          borderColor: '#225AEA',
          borderWidth: 2,
          pointRadius: labels?.length === 1 ? 4 : 0,
          pointHoverRadius: 6,
          pointHoverBorderWidth: 2,
          pointHoverBackgroundColor: 'white',
          yAxisID: 'y',
          fill: true,
          backgroundColor: (context) => {
            const {ctx, chartArea} = context.chart
            if (!chartArea) return undefined
            return getGradient(ctx, chartArea)
          },
        },
        ...(comparisonSeries
          ? [
              {
                label: selected?.comparisonLabel ?? t('previous period'),
                data: comparisonSeries,
                // The chart aligns comparison points to `labels` by index, but each point's own
                // calendar date (e.g. the same day one month back) differs from that index's
                // current-period date - the tooltip needs the real date, so it rides along here
                // rather than through the shared `labels` axis.
                comparisonLabels,
                borderColor: '#A6B6E8',
                borderWidth: 1.5,
                borderDash: [6, 6],
                pointRadius: 0,
                pointHoverRadius: 0,
                yAxisID: 'y',
                fill: false,
                order: 2,
              },
            ]
          : []),
      ],
    }),
    [
      labels,
      selected?.key,
      selected?.label,
      selected?.series,
      comparisonSeries,
      comparisonLabels,
      selected?.comparisonLabel,
      t,
    ]
  )

  if (!tabs.length) return null

  return (
    <KpiTabs tabs={tabs} selectedKey={selected?.key} onSelect={onSelect}>
      <Spin spinning={loading}>
        <Line ref={chartRef} options={chartOptions} data={chartData} height={480} plugins={[tooltipLine]} />
      </Spin>
    </KpiTabs>
  )
}

export default KpiSection
